import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAccessToken } from '../services/authService';
import { PROPERTY_TYPES, AMENITIES_LIST } from '../constants';
import config from '../config';
import AdminBreadcrumb from '../components/AdminBreadcrumb';

// Forward map: i18n key → Vietnamese district name (e.g. "district1" → "Quận 1", "districtBinhThanh" → "Bình Thạnh")
const KEY_TO_VIETNAMESE_DISTRICT = {
  district1: 'Quận 1',
  district2: 'Quận 2',
  district3: 'Quận 3',
  district4: 'Quận 4',
  district5: 'Quận 5',
  district6: 'Quận 6',
  district7: 'Quận 7',
  district8: 'Quận 8',
  district9: 'Quận 9',
  district10: 'Quận 10',
  district11: 'Quận 11',
  district12: 'Quận 12',
  districtBinhThanh: 'Bình Thạnh',
  districtThuDuc: 'Thủ Đức',
  districtGoVap: 'Gò Vấp',
  districtTanBinh: 'Tân Bình',
  districtTanPhu: 'Tân Phú',
  districtPhuNhuan: 'Phú Nhuận',
  districtBinhChanh: 'Bình Chánh',
  districtCuChi: 'Củ Chi',
  districtHocMon: 'Hóc Môn',
  districtNhaBe: 'Nhà Bè',
  districtCanGio: 'Cần Giờ',
};

// Reverse map: Vietnamese district name → i18n key
const VIETNAMESE_DISTRICT_TO_KEY = Object.fromEntries(
  Object.entries(KEY_TO_VIETNAMESE_DISTRICT).map(([key, name]) => [name, key])
);

/**
 * Resolve the best display label for an amenity key using the current i18n settings.
 * e.g. "wifi" → "WiFi miễn phí" (vi) or "Free WiFi" (en)
 */
const getAmenityLabel = (t, amenityKey) => {
  const translation = t(`amenity.${amenityKey}`, { lng: 'vi' });
  // i18next returns the key itself if not found
  if (translation !== `amenity.${amenityKey}`) return translation;
  return amenityKey;
};

const PropertyManagerPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [driveDistricts, setDriveDistricts] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState('');

  const [searchText, setSearchText] = useState('');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [shortTermLeaseFilter, setShortTermLeaseFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [editFiles, setEditFiles] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successPopupMessage, setSuccessPopupMessage] = useState('');

  // Delete property state
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Sync to D1 state
  const [syncLoading, setSyncLoading] = useState(false);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);

  // Secret info modal for viewing/editing secret.txt
  const [showSecretModal, setShowSecretModal] = useState(false);
  const [secretContent, setSecretContent] = useState('');
  const [secretLoading, setSecretLoading] = useState(false);
  const [secretError, setSecretError] = useState('');
  const [isEditingSecret, setIsEditingSecret] = useState(false);
  const [secretEditContent, setSecretEditContent] = useState('');
  const [savingSecret, setSavingSecret] = useState(false);

  // Bulk update state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkPhoneModal, setShowBulkPhoneModal] = useState(false);
  const [bulkNewPhone, setBulkNewPhone] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkUpdateError, setBulkUpdateError] = useState('');

  // Progress overlay state (matching AdminPage create property flow)
  const [showProgress, setShowProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [progressMessage, setProgressMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalUploads, setTotalUploads] = useState(0);

  const PROGRESS_STEPS = {
    UPDATE_ORIGINAL: 'updateOriginal',
    DELETE_ENCRYPTED: 'deleteEncrypted',
    CREATE_ENCRYPTED: 'createEncrypted',
    SECRET_FILE: 'secretFile',
    IMAGES: 'images',
    DONE: 'done',
  };

  const EDIT_STEP_ORDER = [
    PROGRESS_STEPS.UPDATE_ORIGINAL,
    PROGRESS_STEPS.DELETE_ENCRYPTED,
    PROGRESS_STEPS.CREATE_ENCRYPTED,
    PROGRESS_STEPS.SECRET_FILE,
    PROGRESS_STEPS.IMAGES,
    PROGRESS_STEPS.DONE,
  ];

  const updateProgress = (step, message) => {
    setCurrentStep(step);
    setProgressMessage(message);
  };

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const token = getAccessToken();
    if (token) setAccessToken(token);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Use the server-side endpoint with ?admin=1 to bypass in-memory cache
        // This ensures admin pages always see the latest data
        const response = await fetch('/api/drive-all-properties?admin=1');
        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }
        const data = await response.json();
        if (data.error) throw new Error(data.error);

        const flat = [];
        for (const district of data.districts) {
          for (const prop of district.properties) {
            flat.push({
              ...prop,
              districtName: prop.districtName || district.name,
              imageFiles: Array.isArray(prop.images) ? prop.images.map(img => ({
                id: img.id,
                name: img.name,
                url: img.url,
              })) : [],
            });
          }
        }

        setAllProperties(flat);
        setDriveDistricts(data.districts || []);
      } catch (err) {
        setError(err.message || 'Failed to load properties');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [refreshKey]);

  const districtNames = useMemo(() => {
    return [...new Set(allProperties.map((p) => p.districtName))].sort();
  }, [allProperties]);

  const searchSuggestions = useMemo(() => {
    if (!searchText || searchText.length < 1) return [];
    const q = searchText.toLowerCase();
    const seen = new Set();
    const results = [];
    for (const prop of allProperties) {
      const matchTexts = [prop.titleVi, prop.titleEn, prop.districtVi, prop.displayAddressVi, prop.displayAddressEn];
      for (const text of matchTexts) {
        if (text && text.toLowerCase().includes(q) && !seen.has(text)) {
          seen.add(text);
          results.push(text);
        }
      }
    }
    return results.slice(0, 8);
  }, [allProperties, searchText]);

  const filteredProperties = useMemo(() => {
    let list = allProperties;
    if (districtFilter !== 'all') {
      list = list.filter((p) => p.districtName === districtFilter);
    }
    if (typeFilter !== 'all') {
      list = list.filter((p) => (p.propertyType || '').toLowerCase().trim() === typeFilter.toLowerCase().trim());
    }
    if (shortTermLeaseFilter === 'shortTerm') {
      list = list.filter((p) => p.shortTermLease === true);
    } else if (shortTermLeaseFilter === 'longTerm') {
      list = list.filter((p) => p.shortTermLease !== true);
    }
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      list = list.filter((p) =>
        (p.titleVi && p.titleVi.toLowerCase().includes(q)) ||
        (p.titleEn && p.titleEn.toLowerCase().includes(q)) ||
        (p.districtVi && p.districtVi.toLowerCase().includes(q)) ||
        (p.districtEn && p.districtEn.toLowerCase().includes(q)) ||
        (p.displayAddressVi && p.displayAddressVi.toLowerCase().includes(q)) ||
        (p.displayAddressEn && p.displayAddressEn.toLowerCase().includes(q)) ||
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.phone && p.phone.toLowerCase().includes(q))
      );
    }
    // Apply sorting
    if (sortBy === 'price_asc') {
      list = [...list].sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (sortBy === 'price_desc') {
      list = [...list].sort((a, b) => (b.price || 0) - (a.price || 0));
    } else if (sortBy === 'name') {
      list = [...list].sort((a, b) => (a.titleVi || a.name || '').localeCompare(b.titleVi || b.name || ''));
    }
    return list;
  }, [allProperties, districtFilter, typeFilter, shortTermLeaseFilter, searchText, sortBy]);

  // Remove deleted properties from selection
  useEffect(() => {
    const validIds = new Set(allProperties.map(p => p.id));
    setSelectedIds(prev => {
      const next = new Set();
      for (const id of prev) {
        if (validIds.has(id)) next.add(id);
      }
      return next;
    });
  }, [allProperties]);

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  const handleResetFilters = () => {
    setSearchText('');
    setDistrictFilter('all');
    setTypeFilter('all');
    setShortTermLeaseFilter('all');
    setSortBy('name');
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showSecretModal) {
          closeSecretModal();
          return;
        }
        if (showBulkPhoneModal) {
          setShowBulkPhoneModal(false);
          setBulkNewPhone('');
          setBulkUpdateError('');
          return;
        }
        if (isEditing) {
          if (window.confirm('Discard changes?')) {
            setIsEditing(false);
            setEditFormData(null);
            setEditFiles([]);
            setRemovedImageIds([]);
            setEditError('');
          }
        } else {
          setSelectedProperty(null);
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isEditing, showSecretModal, showBulkPhoneModal]);

  /**
   * Read a file's content from Google Drive using OAuth access token.
   */
  const readDriveFileContent = async (fileId) => {
    if (!fileId || !accessToken) return '';
    try {
      const resp = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
        {
          headers: { 'Authorization': `Bearer ${accessToken}` },
        }
      );
      if (!resp.ok) return '';
      return await resp.text();
    } catch {
      return '';
    }
  };

  /**
   * Helper: find a file ID by name in the property folder using OAuth.
   */
  const findFileId = async (folderId, fileName) => {
    if (!folderId || !fileName || !accessToken) return null;
    try {
      const listUrl = `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+name='${fileName}'+and+trashed=false&fields=files(id,name)`;
      const listRes = await fetch(listUrl, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      if (!listRes.ok) return null;
      const listData = await listRes.json();
      const files = listData.files || [];
      return files.length > 0 ? files[0].id : null;
    } catch {
      return null;
    }
  };

  const closeSecretModal = () => {
    setShowSecretModal(false);
    setSecretContent('');
    setSecretError('');
    setIsEditingSecret(false);
    setSecretEditContent('');
  };

  const fetchSecretFile = async (propId) => {
    if (!propId) return;
    setSecretLoading(true);
    setSecretError('');
    setSecretContent('');
    setIsEditingSecret(false);
    setSecretEditContent('');
    try {
      const fileId = await findFileId(propId, 'secret.txt');
      if (!fileId) {
        setSecretContent('(File secret.txt chưa tồn tại)');
        return;
      }
      const text = await readDriveFileContent(fileId);
      if (!text || text.trim() === '') {
        setSecretContent('(Không có nội dung)');
      } else {
        setSecretContent(text);
      }
    } catch (err) {
      setSecretError(err.message || 'Không thể đọc file secret.txt');
    } finally {
      setSecretLoading(false);
    }
  };

  const handleEditSecret = () => {
    setSecretEditContent(secretContent === '(File secret.txt chưa tồn tại)' || secretContent === '(Không có nội dung)' ? '' : secretContent);
    setIsEditingSecret(true);
  };

  const handleCancelSecretEdit = () => {
    setIsEditingSecret(false);
    setSecretEditContent('');
  };

  const handleSaveSecret = async () => {
    if (!selectedProperty || !accessToken) return;
    setSavingSecret(true);
    try {
      const newContent = secretEditContent.trim();

      // Delete old secret.txt if it exists
      const oldSecretFileId = await findFileId(selectedProperty.id, 'secret.txt');
      if (oldSecretFileId) {
        await fetch(`https://www.googleapis.com/drive/v3/files/${oldSecretFileId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${accessToken}` },
        });
      }

      if (newContent) {
        // Create new secret.txt with updated content
        const createSecretRes = await fetch('/api/admin-create-file', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ parentId: selectedProperty.id, fileName: 'secret.txt', content: newContent }),
        });
        if (!createSecretRes.ok) {
          const errData = await createSecretRes.json();
          throw new Error(errData.error || 'Failed to save secret.txt');
        }

        // Restrict permissions on the new secret.txt (remove public access)
        const secretFileData = await createSecretRes.json();
        try {
          const permsRes = await fetch(
            `https://www.googleapis.com/drive/v3/files/${secretFileData.id}/permissions?fields=permissions(id,role)`,
            { headers: { 'Authorization': `Bearer ${accessToken}` } }
          );
          if (permsRes.ok) {
            const permsData = await permsRes.json();
            const permissions = permsData.permissions || [];
            for (const perm of permissions) {
              if (perm.role !== 'owner') {
                await fetch(
                  `https://www.googleapis.com/drive/v3/files/${secretFileData.id}/permissions/${perm.id}`,
                  {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${accessToken}` },
                  }
                );
              }
            }
          }
        } catch (permErr) {
          console.warn('[PropertyManager] Could not remove public permissions from secret.txt', permErr);
        }

        setSecretContent(newContent);
      } else {
        setSecretContent('(File secret.txt đã bị xoá)');
      }

      setIsEditingSecret(false);
      setSecretEditContent('');
    } catch (err) {
      setSecretError(err.message || 'Failed to save secret.txt');
    } finally {
      setSavingSecret(false);
    }
  };

  const openEdit = () => {
    const prop = selectedProperty;
    if (!prop) return;

    const districtKey = VIETNAMESE_DISTRICT_TO_KEY[prop.districtVi] || prop.districtVi || '';

    setEditFormData({
      titleVi: prop.titleVi || '',
      titleEn: prop.titleEn || '',
      descriptionVi: prop.descriptionVi || '',
      descriptionEn: prop.descriptionEn || '',
      displayAddressVi: prop.displayAddressVi || prop.addressVi || '',
      displayAddressEn: prop.displayAddress || prop.addressEn || '',
      addressVi: prop.displayAddressVi || prop.addressVi || '',
      addressEn: prop.displayAddress || prop.addressEn || '',
      additionalAddress1Vi: prop.additionalAddress1Vi || prop.additionalAddress1 || '',
      additionalAddress1En: prop.additionalAddress1 || prop.additionalAddress1Vi || '',
      additionalAddress2Vi: prop.additionalAddress2Vi || prop.additionalAddress2 || '',
      additionalAddress2En: prop.additionalAddress2 || prop.additionalAddress2Vi || '',
      cityVi: prop.cityVi || 'Hồ Chí Minh',
      cityEn: prop.cityEn || 'Ho Chi Minh City',
      districtVi: districtKey,
      districtEn: prop.districtEn || prop.districtName || '',
      price: prop.price || '',
      phone: prop.phone || '',
      email: prop.email || '',
      propertyType: prop.propertyType || '',
      amenities: [...(prop.amenities || [])],
      available: prop.available !== false,
      shortTermLease: prop.shortTermLease === true,
      nearbyVi: prop.nearbyVi ? prop.nearbyVi.join(', ') : '',
      nearbyEn: prop.nearbyEn ? prop.nearbyEn.join(', ') : '',
    });
    setIsEditing(true);
    setEditFiles([]);
    setRemovedImageIds([]);
    setEditError('');
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      if (name === 'titleVi' && (!prev.titleEn || !prev.titleEn.trim())) {
        updated.titleEn = value;
      } else if (name === 'descriptionVi' && value && (!prev.descriptionEn || !prev.descriptionEn.trim())) {
        updated.descriptionEn = value;
      } else if (name === 'displayAddressVi' && (!prev.displayAddressEn || !prev.displayAddressEn.trim())) {
        updated.displayAddressEn = value;
        updated.addressEn = value;
      } else if (name === 'cityVi' && (!prev.cityEn || !prev.cityEn.trim())) {
        updated.cityEn = value;
      } else if (name === 'nearbyVi' && (!prev.nearbyEn || !prev.nearbyEn.trim())) {
        updated.nearbyEn = value;
      }
      if (name === 'additionalAddress1Vi' && (!prev.additionalAddress1En || !prev.additionalAddress1En.trim())) {
        updated.additionalAddress1En = value;
      }
      if (name === 'additionalAddress2Vi' && (!prev.additionalAddress2En || !prev.additionalAddress2En.trim())) {
        updated.additionalAddress2En = value;
      }
      if (name === 'displayAddressVi') {
        updated.addressVi = value;
      }
      if (name === 'displayAddressEn') {
        updated.addressEn = value;
      }
      return updated;
    });
  };

  const handleEditAmenityToggle = (amenityKey) => {
    setEditFormData((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenityKey)
        ? prev.amenities.filter((a) => a !== amenityKey)
        : [...prev.amenities, amenityKey],
    }));
  };

  const handleEditFileChange = (e) => {
    const files = Array.from(e.target.files);
    const mediaFiles = files.filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'));
    setEditFiles((prev) => [...prev, ...mediaFiles]);
    e.target.value = '';
  };

  const handleRemoveExistingImage = (imageId) => {
    setRemovedImageIds((prev) => [...prev, imageId]);
  };

  const handleRemoveNewFile = (index) => {
    setEditFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadSingleImage = async (file, parentId, token) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('parentId', parentId);
    const response = await fetch('/api/admin-upload-image', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: fd,
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to upload ${file.name}`);
    }
    return response.json();
  };

  const validateForm = (data) => {
    const errors = {};
    if (!data.titleVi || !data.titleVi.trim()) errors.titleVi = 'Vui lòng nhập tiêu đề (VI)';
    if (!data.descriptionVi || !data.descriptionVi.trim()) errors.descriptionVi = 'Vui lòng nhập mô tả (VI)';
    if (!data.addressVi || !data.addressVi.trim()) errors.addressVi = 'Vui lòng nhập địa chỉ (VI)';
    if (!data.districtVi) errors.districtVi = 'Vui lòng chọn quận';
    if (!data.price || isNaN(data.price) || Number(data.price) <= 0) errors.price = 'Vui lòng nhập giá hợp lệ';
    if (!data.propertyType) errors.propertyType = 'Vui lòng chọn loại phòng';
    if (!data.phone || !data.phone.trim()) errors.phone = 'Vui lòng nhập số điện thoại';
    return errors;
  };

  const handleSaveEdit = async () => {
    if (!editFormData || !selectedProperty) return;
    if (!accessToken) {
      setEditError('No access token. Please re-authenticate.');
      return;
    }

    const errors = validateForm(editFormData);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSaving(false);
      return;
    }
    setSaving(true);
    setEditError('');
    setShowProgress(true);

    try {
      const prop = selectedProperty;
      const f = editFormData;
      const districtViDisplay = KEY_TO_VIETNAMESE_DISTRICT[f.districtVi] || f.districtVi;

      const displayAddressEn = f.displayAddressEn || f.addressEn || f.addressVi || f.titleVi;
      const displayAddressVi = f.displayAddressVi || f.addressVi || f.addressEn || f.titleVi;

      const propertyContent = `[EN]
title: ${f.titleEn || f.titleVi}
description: ${JSON.stringify(f.descriptionEn || f.descriptionVi)}
displayAddress: ${displayAddressEn}
city: ${f.cityEn || f.cityVi}
district: ${f.districtEn || districtViDisplay}
type: ${f.propertyType}
price: ${f.price}
phone: ${f.phone}
email: ${f.email}
available: ${f.available}
shortTermLease: ${f.shortTermLease}
amenities: ${f.amenities.join(', ')}
nearby: ${f.nearbyEn || f.nearbyVi}
additionalAddress1: ${f.additionalAddress1En || f.additionalAddress1Vi || ''}
additionalAddress2: ${f.additionalAddress2En || f.additionalAddress2Vi || ''}

[VI]
title: ${f.titleVi}
description: ${JSON.stringify(f.descriptionVi)}
displayAddress: ${displayAddressVi}
city: ${f.cityVi}
district: ${districtViDisplay}
type: ${f.propertyType}
price: ${f.price}
phone: ${f.phone}
email: ${f.email}
available: ${f.available}
shortTermLease: ${f.shortTermLease}
amenities: ${f.amenities.join(', ')}
nearby: ${f.nearbyVi}
additionalAddress1: ${f.additionalAddress1Vi || ''}
additionalAddress2: ${f.additionalAddress2Vi || ''}
`;

      // Delete removed images first
      for (const imageId of removedImageIds) {
        try {
          await fetch(`https://www.googleapis.com/drive/v3/files/${imageId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${accessToken}` },
          });
        } catch (err) {
          console.warn(`[PropertyManager] Failed to delete image ${imageId}:`, err);
        }
      }

      // Upload new images
      let uploadedCount = 0;
      const newEditImages = [];
      if (editFiles.length > 0) {
        setTotalUploads(editFiles.length);
        updateProgress(PROGRESS_STEPS.IMAGES, `${t('admin.progressImages', { count: `0/${editFiles.length}` })}`);

        for (let i = 0; i < editFiles.length; i++) {
          const result = await uploadSingleImage(editFiles[i], prop.id, accessToken);
          newEditImages.push({ id: result.id, name: editFiles[i].name });
          uploadedCount = i + 1;
          updateProgress(PROGRESS_STEPS.IMAGES, `${t('admin.progressImages', { count: `${uploadedCount}/${editFiles.length}` })}`);
          setUploadProgress(Math.round(((i + 1) / editFiles.length) * 100));
        }
      }

      // Step 1: Update property_original.txt via PATCH
      updateProgress(PROGRESS_STEPS.UPDATE_ORIGINAL, t('admin.progressPropertyFile'));
      const originalFileId = await findFileId(prop.id, 'property_original.txt');
      if (originalFileId) {
        const updateRes = await fetch(`https://www.googleapis.com/upload/drive/v3/files/${originalFileId}?uploadType=media`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'text/plain' },
          body: propertyContent,
        });
        if (!updateRes.ok) {
          const errData = await updateRes.json();
          throw new Error(errData.error?.message || 'Failed to update property_original.txt');
        }
      } else {
        const createRes = await fetch('/api/admin-create-file', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ parentId: prop.id, fileName: 'property_original.txt', content: propertyContent }),
        });
        if (!createRes.ok) {
          const errData = await createRes.json();
          throw new Error(errData.error || 'Failed to create property_original.txt');
        }
      }

      // Step 2: Delete existing property.txt if it exists
      updateProgress(PROGRESS_STEPS.DELETE_ENCRYPTED, t('admin.progressDeleteEncrypted'));
      const encryptedFileId = await findFileId(prop.id, 'property.txt');
      if (encryptedFileId) {
        await fetch(`https://www.googleapis.com/drive/v3/files/${encryptedFileId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${accessToken}` },
        });
      }

      // Step 3: Create a new property.txt with the updated content (fresh encrypt)
      updateProgress(PROGRESS_STEPS.CREATE_ENCRYPTED, t('admin.progressCreateEncrypted'));
      const createEncryptRes = await fetch('/api/admin-create-file', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentId: prop.id, fileName: 'property.txt', content: propertyContent, encrypt: true }),
      });
      if (!createEncryptRes.ok) {
        const errData = await createEncryptRes.json();
        throw new Error(errData.error || 'Failed to create property.txt');
      }

      // Build the full image list
      const existingImages = (prop.imageFiles || [])
        .filter(img => !removedImageIds.includes(img.id))
        .map(img => ({ id: img.id, name: img.name || 'image' }));
      const allImages = [...existingImages, ...newEditImages];

      // Build additionalAddresses array
      const addrs = [];
      if (f.additionalAddress1Vi || f.additionalAddress1En) addrs.push(f.additionalAddress1Vi || f.additionalAddress1En);
      if (f.additionalAddress2Vi || f.additionalAddress2En) addrs.push(f.additionalAddress2Vi || f.additionalAddress2En);
      if (displayAddressVi) addrs.push(displayAddressVi);

      // Sync to D1 cache
      try {
        await fetch('/api/admin-sync-property', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: prop.id,
            name: f.titleVi || prop.name,
            districtName: districtViDisplay || prop.districtName,
            imageFiles: allImages,
            titleVi: f.titleVi,
            titleEn: f.titleEn,
            descriptionVi: f.descriptionVi,
            descriptionEn: f.descriptionEn,
            displayAddressVi: displayAddressVi,
            displayAddressEn: displayAddressEn,
            additionalAddress1: f.additionalAddress1Vi || f.additionalAddress1En || '',
            additionalAddress1En: f.additionalAddress1En || '',
            additionalAddress2: f.additionalAddress2Vi || f.additionalAddress2En || '',
            additionalAddress2En: f.additionalAddress2En || '',
            additionalAddresses: addrs,
            addressVi: f.displayAddressVi || f.addressVi || '',
            addressEn: f.displayAddressEn || f.addressEn || '',
            cityVi: f.cityVi,
            cityEn: f.cityEn,
            districtVi: districtViDisplay,
            districtEn: f.districtEn || districtViDisplay,
            price: parseInt(f.price) || 0,
            phone: f.phone,
            email: f.email,
            propertyType: f.propertyType,
            amenities: f.amenities,
            available: f.available,
            shortTermLease: f.shortTermLease,
            nearbyVi: f.nearbyVi ? f.nearbyVi.split(',').map(s => s.trim()) : [],
            nearbyEn: f.nearbyEn ? f.nearbyEn.split(',').map(s => s.trim()) : [],
          }),
        });
        console.log('[PropertyManager] Synced to D1 cache');
      } catch (syncErr) {
        console.warn('[PropertyManager] Failed to sync to D1 cache:', syncErr);
      }

      updateProgress(PROGRESS_STEPS.DONE, t('admin.progressDone'));
      await new Promise(resolve => setTimeout(resolve, 800));

      setShowProgress(false);
      setIsEditing(false);
      setEditFormData(null);
      setEditFiles([]);
      setRemovedImageIds([]);
      setSelectedProperty(null);
      setRefreshKey((k) => k + 1);
      setSuccessPopupMessage(`"${f.titleVi}" đã được cập nhật thành công!`);
      setShowSuccessPopup(true);
    } catch (err) {
      setEditError(err.message || 'Failed to save changes');
      setShowProgress(false);
    } finally {
      setSaving(false);
      setShowProgress(false);
      setCurrentStep('');
      setUploadProgress(0);
      setTotalUploads(0);
    }
  };

  const cancelEdit = () => {
    if (window.confirm('Discard changes?')) {
      setIsEditing(false);
      setEditFormData(null);
      setEditFiles([]);
      setRemovedImageIds([]);
      setEditError('');
    }
  };

  // ── Bulk selection handlers ──

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredProperties.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredProperties.map(p => p.id)));
    }
  };

  const toggleSelectProperty = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const openBulkPhoneModal = () => {
    setBulkNewPhone('');
    setBulkUpdateError('');
    setShowBulkPhoneModal(true);
  };

  const handleBulkUpdatePhone = async () => {
    if (!bulkNewPhone.trim()) {
      setBulkUpdateError('Vui lòng nhập số điện thoại mới');
      return;
    }
    if (!accessToken) {
      setBulkUpdateError('No access token. Please re-authenticate.');
      return;
    }

    setBulkUpdating(true);
    setBulkUpdateError('');

    try {
      const selectedProperties = allProperties
        .filter(p => selectedIds.has(p.id))
        .map(p => ({
          id: p.id,
          name: p.titleVi || p.name || p.id,
          districtName: p.districtVi || p.districtName || 'Unknown',
          newPhone: bulkNewPhone.trim(),
        }));

      const response = await fetch('/api/admin-bulk-update-phone', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ properties: selectedProperties }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Bulk update failed');
      }

      setShowBulkPhoneModal(false);
      setBulkNewPhone('');
      setSelectedIds(new Set());

      if (result.failed === 0) {
        setSuccessPopupMessage(t('admin.bulkSuccess', { count: result.successful }));
      } else {
        setSuccessPopupMessage(t('admin.bulkPartialSuccess', { success: result.successful, failed: result.failed }));
      }
      setShowSuccessPopup(true);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setBulkUpdateError(err.message || 'Bulk update failed');
    } finally {
      setBulkUpdating(false);
    }
  };

  const SYNC_STEPS = [
    'readingDrive',   // Đang đọc dữ liệu từ Google Drive...
    'savingD1',       // Đang lưu vào cơ sở dữ liệu D1...
    'done',           // Hoàn tất!
  ];

  const handleSyncAll = async () => {
    setShowSyncConfirm(false);
    setSyncLoading(true);
    setShowProgress(true);
    updateProgress(SYNC_STEPS[0], t('admin.syncProgress'));

    try {
      if (!accessToken) {
        throw new Error(t('admin.errorNoToken'));
      }

      updateProgress(SYNC_STEPS[0], t('admin.syncProgress'));

      const response = await fetch('/api/admin-sync-all', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('common.error'));
      }

      if (data.success) {
        updateProgress(SYNC_STEPS[1], t('admin.syncProgress'));
        // Small delay so user sees "saving" step
        await new Promise(resolve => setTimeout(resolve, 500));

        setRefreshKey((k) => k + 1);

        updateProgress(SYNC_STEPS[2], t('admin.progressDone'));
        await new Promise(resolve => setTimeout(resolve, 600));

        setShowProgress(false);
        setSuccessPopupMessage(t('admin.syncSuccess', { count: data.totalProperties, districts: data.totalDistricts || data.districts.length }));
        setShowSuccessPopup(true);
      } else {
        throw new Error(data.error || t('common.error'));
      }
    } catch (err) {
      setShowProgress(false);
      setSuccessPopupMessage(t('admin.syncError', { msg: err.message }));
      setShowSuccessPopup(true);
    } finally {
      setSyncLoading(false);
      setCurrentStep('');
    }
  };

  const handleDeleteProperty = async () => {
    if (!selectedProperty || !accessToken) return;
    setDeleting(true);
    setShowDeleteConfirm(false);
    try {
      const response = await fetch('/api/admin-delete-property', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: selectedProperty.id }),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to delete property');
      }
      setSuccessPopupMessage(`"${selectedProperty.titleVi || selectedProperty.name}" đã được xoá thành công!`);
      setShowSuccessPopup(true);
      setSelectedProperty(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setEditError(err.message || 'Failed to delete property');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-96 mb-8" />
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-20 bg-gray-100 rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 text-center">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('common.error')}</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button onClick={handleRefresh} className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
              {t('common.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <AdminBreadcrumb />

        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Quản lý nhà trọ</h1>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {t('dashboard.title')} · {allProperties.length} {t('dashboard.managedProperties.total')?.toLowerCase() || 'properties'} · {driveDistricts.length} {t('about.form.district')?.toLowerCase() || 'districts'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleRefresh} className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors" title={t('dashboard.refresh')}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={() => setShowSyncConfirm(true)}
                disabled={syncLoading || !accessToken}
                className="p-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                title={t('admin.syncToD1')}
              >
                {syncLoading ? (
                  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16l4-4 4 4" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12v7" />
                  </svg>
                )}
              </button>
              {/* Bulk Update Phone Button */}
              {selectedIds.size > 0 && (
                <button
                  onClick={openBulkPhoneModal}
                  className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                  title={t('admin.bulkUpdatePhone')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {t('admin.bulkUpdatePhone')} ({selectedIds.size})
                </button>
              )}
              <button onClick={() => navigate('/admin/create-property')} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t('admin.title')}
              </button>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="mt-6 space-y-4">
            {/* Row 1: District + Search (autocomplete) + Reset */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('about.form.district')}</label>
                <select value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                  <option value="all">{t('dashboard.period.all')}</option>
                  {districtNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="relative lg:col-span-2" ref={searchRef}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tìm kiếm</label>
                <div className="relative">
                  <input type="text" value={searchText} onChange={(e) => { setSearchText(e.target.value); setShowSuggestions(true); }} onFocus={() => setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} placeholder="Tìm theo tên, quận, địa chỉ..." className="w-full px-4 py-2.5 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {showSuggestions && searchSuggestions.length > 0 && searchText.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {searchSuggestions.map((suggestion, idx) => (
                      <button key={idx} onMouseDown={() => { setSearchText(suggestion); setShowSuggestions(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors">{suggestion}</button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-end">
                <button onClick={handleResetFilters} className="w-full px-6 py-2.5 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 border border-gray-300 rounded-lg transition-colors">{t('filter.reset')}</button>
              </div>
            </div>
            {/* Row 2: Property Type + ShortTermLease + Sort */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Loại phòng</label>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                  <option value="all">Tất cả</option>
                  {PROPERTY_TYPES.map((pt) => (
                    <option key={pt.value} value={pt.value}>{pt.value}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.shortTermLease')}</label>
                <select value={shortTermLeaseFilter} onChange={(e) => setShortTermLeaseFilter(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                  <option value="all">Tất cả</option>
                  <option value="shortTerm">Cho thuê ngắn ngày</option>
                  <option value="longTerm">Cho thuê dài hạn</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sắp xếp</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                  <option value="name">Tên A-Z</option>
                  <option value="price_asc">Giá tăng dần</option>
                  <option value="price_desc">Giá giảm dần</option>
                </select>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-sm text-gray-500">Hiển thị {filteredProperties.length} / {allProperties.length} nhà trọ</p>
            {selectedIds.size > 0 && (
              <p className="text-sm text-indigo-600 font-medium">
                {t('admin.selectedCount', { count: selectedIds.size })}
              </p>
            )}
          </div>
        </div>

        {/* Properties Grid */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {filteredProperties.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p className="text-lg">Không tìm thấy nhà trọ</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                      <input
                        type="checkbox"
                        checked={filteredProperties.length > 0 && selectedIds.size === filteredProperties.length}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tên nhà trọ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('about.form.district')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Loại</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Giá</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Điện thoại</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ảnh</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trạng thái</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProperties.map((prop, index) => (
                    <tr
                      key={prop.id}
                      className={`hover:bg-gray-50 cursor-pointer transition-colors ${selectedIds.has(prop.id) ? 'bg-indigo-50/50' : ''}`}
                      onClick={() => { setSelectedProperty(prop); setIsEditing(false); setEditFormData(null); setEditFiles([]); setRemovedImageIds([]); }}
                    >
                      <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(prop.id)}
                          onChange={() => toggleSelectProperty(prop.id)}
                          className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium max-w-[250px] truncate">{prop.titleVi || prop.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{prop.districtVi || prop.districtName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{prop.propertyType || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{prop.price > 0 ? new Intl.NumberFormat('vi-VN').format(prop.price) + '₫' : '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{prop.phone || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {Array.isArray(prop.imageFiles) && prop.imageFiles.length > 0 ? (
                          <div className="flex items-center gap-1">
                            <span className="text-gray-600">{prop.imageFiles.length}</span>
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        ) : <span className="text-gray-400">0</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${prop.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {prop.available ? 'Còn trống' : 'Đã thuê'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button onClick={(e) => { e.stopPropagation(); setSelectedProperty(prop); setIsEditing(false); setEditFormData(null); setEditFiles([]); setRemovedImageIds([]); }} className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors" title="Xem chi tiết">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedProperty && !isEditing && (
        <PropertyDetailModal
          property={selectedProperty}
          onClose={() => setSelectedProperty(null)}
          onEdit={openEdit}
          onDelete={() => setShowDeleteConfirm(true)}
          t={t}
          getAmenityLabel={getAmenityLabel}
          onViewSecret={() => {
            setShowSecretModal(true);
            fetchSecretFile(selectedProperty.id);
          }}
          hasSecretFile={true}
        />
      )}

      {/* Edit Modal */}
      {selectedProperty && isEditing && editFormData && (
        <EditPropertyModal
          property={selectedProperty}
          formData={editFormData}
          onInputChange={handleEditInputChange}
          onAmenityToggle={handleEditAmenityToggle}
          onFileChange={handleEditFileChange}
          onRemoveExistingImage={handleRemoveExistingImage}
          onRemoveNewFile={handleRemoveNewFile}
          removedImageIds={removedImageIds}
          newFiles={editFiles}
          onSave={handleSaveEdit}
          onCancel={cancelEdit}
          onEditSecret={() => {
            setShowSecretModal(true);
            fetchSecretFile(selectedProperty.id);
          }}
          saving={saving}
          error={editError}
          fieldErrors={fieldErrors}
          t={t}
          getAmenityLabel={getAmenityLabel}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedProperty && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Xác nhận xoá</h3>
                  <p className="text-sm text-gray-500">Bạn có chắc chắn muốn xoá nhà trọ này?</p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-amber-800">Hành động này sẽ:</p>
                    <ul className="text-xs text-amber-700 mt-1 list-disc ml-4 space-y-0.5">
                      <li>Di chuyển thư mục Google Drive vào thùng rác</li>
                      <li>Xoá bản ghi trong D1 cache</li>
                      <li>Không thể khôi phục trực tiếp từ ứng dụng</li>
                    </ul>
                  </div>
                </div>
              </div>
              <p className="text-sm font-semibold text-gray-900 mb-4">
                "{selectedProperty.titleVi || selectedProperty.name}" ({selectedProperty.districtVi || selectedProperty.districtName})
              </p>
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} disabled={deleting} className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">Huỷ</button>
              <button onClick={handleDeleteProperty} disabled={deleting} className="px-6 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                {deleting ? (
                  <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Đang xoá...</>
                ) : 'Xoá nhà trọ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Confirmation Modal */}
      {showSyncConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setShowSyncConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{t('admin.syncToD1')}</h3>
                  <p className="text-sm text-gray-500">{t('admin.syncToD1Desc')}</p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-amber-800">Hành động này sẽ:</p>
                    <ul className="text-xs text-amber-700 mt-1 list-disc ml-4 space-y-0.5">
                      <li>Đọc tất cả phòng trọ từ Google Drive</li>
                      <li>Ghi đè toàn bộ dữ liệu trong D1 database</li>
                      <li>Thay thế bộ nhớ đệm hiện tại</li>
                    </ul>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Bạn có muốn tiếp tục đồng bộ tất cả {allProperties.length} phòng trọ từ {driveDistricts.length} quận vào D1?
              </p>
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setShowSyncConfirm(false)} disabled={syncLoading} className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">Huỷ</button>
              <button onClick={handleSyncAll} disabled={syncLoading} className="px-6 py-2.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                {syncLoading ? (
                  <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Đang đồng bộ...</>
                ) : t('admin.syncButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Phone Update Modal */}
      {showBulkPhoneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => { if (!bulkUpdating) { setShowBulkPhoneModal(false); setBulkNewPhone(''); setBulkUpdateError(''); } }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{t('admin.bulkUpdatePhone')}</h3>
                  <p className="text-sm text-gray-500">{t('admin.bulkUpdateDesc')}</p>
                </div>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 mb-4">
                <p className="text-sm font-medium text-indigo-800">
                  {t('admin.selectedCount', { count: selectedIds.size })}
                </p>
              </div>

              {bulkUpdateError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">{bulkUpdateError}</div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.bulkPhoneLabel')}</label>
                <input
                  type="tel"
                  value={bulkNewPhone}
                  onChange={(e) => setBulkNewPhone(e.target.value)}
                  placeholder={t('admin.bulkPhonePlaceholder')}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  autoFocus
                  disabled={bulkUpdating}
                />
              </div>
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => { setShowBulkPhoneModal(false); setBulkNewPhone(''); setBulkUpdateError(''); }} disabled={bulkUpdating} className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                {t('common.cancel')}
              </button>
              <button onClick={handleBulkUpdatePhone} disabled={bulkUpdating || !bulkNewPhone.trim()} className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                {bulkUpdating ? (
                  <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>{t('admin.bulkUpdating')}</>
                ) : t('admin.bulkUpdateBtn', { count: selectedIds.size })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Secret Info Modal - combined view/edit */}
      {showSecretModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => { if (!isEditingSecret) closeSecretModal(); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{isEditingSecret ? 'Chỉnh sửa thông tin bí mật' : 'Thông tin bí mật'}</h3>
                  <p className="text-xs text-gray-500">{selectedProperty?.titleVi || selectedProperty?.name}</p>
                </div>
              </div>
              <button onClick={closeSecretModal} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-5">
              {secretLoading ? (
                <div className="flex items-center gap-3 py-8 justify-center">
                  <svg className="animate-spin h-5 w-5 text-primary-600" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-gray-600">Đang tải nội dung...</span>
                </div>
              ) : secretError ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{secretError}</div>
              ) : isEditingSecret ? (
                <textarea
                  value={secretEditContent}
                  onChange={(e) => setSecretEditContent(e.target.value)}
                  rows="8"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                  placeholder="Nhập thông tin bí mật..."
                />
              ) : (
                <pre className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-xl p-4 border border-gray-200 max-h-[50vh] overflow-y-auto">{secretContent}</pre>
              )}
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              {isEditingSecret ? (
                <>
                  <button onClick={handleCancelSecretEdit} disabled={savingSecret} className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">Huỷ</button>
                  <button onClick={handleSaveSecret} disabled={savingSecret} className="px-6 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                    {savingSecret ? (
                      <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Đang lưu...</>
                      ) : 'Lưu thay đổi'}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={closeSecretModal} className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors">Đóng</button>
                  <button onClick={handleEditSecret} className="px-6 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Chỉnh sửa
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Animated Progress Overlay */}
      {showProgress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-sm w-full mx-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('admin.progressTitle')}</h3>
              <div className="space-y-4">
                {/* Show sync steps when syncing, otherwise show edit steps */}
                {(SYNC_STEPS.includes(currentStep) ? SYNC_STEPS : EDIT_STEP_ORDER).map((step) => {
                  const steps = SYNC_STEPS.includes(currentStep) ? SYNC_STEPS : EDIT_STEP_ORDER;
                  const stepIndex = steps.indexOf(step);
                  const currentIndex = steps.indexOf(currentStep);
                  const isActive = step === currentStep;
                  const isCompleted = stepIndex < currentIndex;

                  let stepIcon = (
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-gray-300" />
                    </div>
                  );

                  if (isActive) {
                    stepIcon = (
                      <div className="w-6 h-6 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
                    );
                  } else if (isCompleted) {
                    stepIcon = (
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    );
                  }

                  let message;
                  if (step === PROGRESS_STEPS.UPDATE_ORIGINAL) message = t('admin.progressPropertyFile');
                  else if (step === PROGRESS_STEPS.DELETE_ENCRYPTED) message = t('admin.progressDeleteEncrypted');
                  else if (step === PROGRESS_STEPS.CREATE_ENCRYPTED) message = t('admin.progressCreateEncrypted');
                  else if (step === PROGRESS_STEPS.SECRET_FILE) message = t('admin.progressSecretFile');
                  else if (step === PROGRESS_STEPS.IMAGES) message = isActive ? progressMessage : t('admin.progressImages', { count: `${totalUploads}/${totalUploads}` });
                  else if (step === PROGRESS_STEPS.DONE) message = isActive || isCompleted ? t('admin.progressDone') : '';
                  // Sync steps
                  else if (step === SYNC_STEPS[0]) message = progressMessage || t('admin.syncProgress');
                  else if (step === SYNC_STEPS[1]) message = t('admin.syncProgress');
                  else if (step === SYNC_STEPS[2]) message = isActive || isCompleted ? t('admin.progressDone') : '';

                  return (
                    <div key={step} className="flex items-center gap-3">
                      <div className="flex-shrink-0">{stepIcon}</div>
                      <div className="flex-1 text-left">
                        <span className={`text-sm ${isActive ? 'text-primary-600 font-medium' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                          {message}
                        </span>
                        {step === PROGRESS_STEPS.IMAGES && isActive && totalUploads > 0 && (
                          <div className="mt-1.5 w-full bg-gray-200 rounded-full h-1.5">
                            <div className="bg-primary-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 text-center animate-scale-in">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">✅ {t('admin.progressDone')}</h3>
            <p className="text-gray-600 mb-6">{successPopupMessage}</p>
            <button
              onClick={() => setShowSuccessPopup(false)}
              className="bg-primary-600 text-white px-6 py-2.5 rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Property Detail Modal (View Mode)
 */
const PropertyDetailModal = ({ property, onClose, onEdit, onDelete, t, getAmenityLabel, onViewSecret, hasSecretFile }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => { requestAnimationFrame(() => setIsVisible(true)); }, []);

  const handleClose = () => { setIsVisible(false); setTimeout(onClose, 200); };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'N/A';
    return new Intl.NumberFormat('vi-VN').format(price) + '₫';
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-200 ${isVisible ? 'bg-black/40 backdrop-blur-sm' : 'bg-black/0'}`} onClick={handleClose}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto transition-all duration-300 ${isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}`} onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{property.titleVi || property.name}</h3>
              <p className="text-xs text-gray-500">{property.districtVi || property.districtName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Chỉnh sửa
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors" title="Xoá nhà trọ">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">
          {Array.isArray(property.imageFiles) && property.imageFiles.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-3">Ảnh ({property.imageFiles.length})</p>
              <div className="flex flex-wrap gap-2">
                {property.imageFiles.map((img) => (
                  <div key={img.id} className="relative group">
                    <img src={img.url} alt={img.name} className="w-24 h-24 object-cover rounded-lg border border-gray-200" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{property.titleEn ? 'Tiêu đề (VI)' : 'Tiêu đề'}</p>
              <p className="text-base font-semibold text-gray-900">{property.titleVi || property.name}</p>
            </div>
            {property.titleEn && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Tiêu đề (EN)</p>
                <p className="text-base font-semibold text-gray-900">{property.titleEn}</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Mô tả (VI)</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{property.descriptionVi || '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Mô tả (EN)</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{property.descriptionEn || '—'}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Địa chỉ hiển thị (VI)</p>
              <p className="text-sm text-gray-800">{property.displayAddressVi || property.addressVi || '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Địa chỉ hiển thị (EN)</p>
              <p className="text-sm text-gray-800">{property.displayAddressEn || property.addressEn || '—'}</p>
            </div>
          </div>

          {(property.additionalAddress1Vi || property.additionalAddress1 || property.additionalAddress2Vi || property.additionalAddress2) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Địa chỉ bổ sung 1</p>
                <p className="text-sm text-gray-800">{property.additionalAddress1Vi || property.additionalAddress1 || '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Địa chỉ bổ sung 2</p>
                <p className="text-sm text-gray-800">{property.additionalAddress2Vi || property.additionalAddress2 || '—'}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Quận (VI)</p>
              <p className="text-sm font-medium text-gray-900">{property.districtVi || property.districtName}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Quận (EN)</p>
              <p className="text-sm font-medium text-gray-900">{property.districtEn || property.districtName}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Thành phố (VI)</p>
              <p className="text-sm font-medium text-gray-900">{property.cityVi || '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Thành phố (EN)</p>
              <p className="text-sm font-medium text-gray-900">{property.cityEn || '—'}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Giá</p>
              <p className="text-base font-semibold text-primary-600">{formatPrice(property.price)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Loại</p>
              <p className="text-sm font-medium text-gray-900">{property.propertyType || '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Điện thoại</p>
              <a href={`tel:${property.phone}`} className="text-sm font-medium text-primary-600 hover:underline">{property.phone || '—'}</a>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Email</p>
              <p className="text-sm font-medium text-gray-900">{property.email || '—'}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 text-sm font-semibold rounded-full ${property.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {property.available ? 'Còn trống' : 'Đã thuê'}
            </span>
            {property.shortTermLease && (
              <span className="px-3 py-1.5 text-sm font-semibold rounded-full bg-amber-100 text-amber-800">
                Cho thuê ngắn ngày
              </span>
            )}
          </div>

          {hasSecretFile && (
            <div>
              <button
                onClick={(e) => { e.stopPropagation(); onViewSecret(); }}
                className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Xem thông tin bí mật
              </button>
            </div>
          )}

          {Array.isArray(property.amenities) && property.amenities.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-3">Tiện ích ({property.amenities.length})</p>
              <div className="flex flex-wrap gap-2">
                {property.amenities.map((amenity, i) => (
                  <span key={i} className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium">
                    {getAmenityLabel(t, amenity)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(property.nearbyVi?.length > 0 || property.nearbyEn?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {property.nearbyVi?.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Địa điểm lân cận (VI)</p>
                  <p className="text-sm text-gray-800">{Array.isArray(property.nearbyVi) ? property.nearbyVi.join(', ') : property.nearbyVi}</p>
                </div>
              )}
              {property.nearbyEn?.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Địa điểm lân cận (EN)</p>
                  <p className="text-sm text-gray-800">{Array.isArray(property.nearbyEn) ? property.nearbyEn.join(', ') : property.nearbyEn}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 px-6 py-4 flex justify-end">
          <button onClick={handleClose} className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors">{t('common.close')}</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Edit Property Modal - with translated amenity labels, NO secret info field
 */
const EditPropertyModal = ({
  property, formData, onInputChange, onAmenityToggle, onFileChange,
  onRemoveExistingImage, onRemoveNewFile, removedImageIds, newFiles,
  onSave, onCancel, onEditSecret, saving, error, t, getAmenityLabel,
  fieldErrors = {},
}) => {
  const fileInputRef = useRef(null);

  const existingImages = useMemo(() => {
    if (!Array.isArray(property.imageFiles)) return [];
    return property.imageFiles.filter((img) => !removedImageIds.includes(img.id));
  }, [property.imageFiles, removedImageIds]);

  const inputClass = (fieldName) => {
    const base = 'w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500';
    return fieldErrors[fieldName]
      ? `${base} border-red-400 bg-red-50 focus:border-red-500`
      : `${base} border-gray-300`;
  };

  const errorText = (fieldName) => {
    return fieldErrors[fieldName]
      ? <p className="mt-1 text-xs text-red-600">{fieldErrors[fieldName]}</p>
      : null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Chỉnh sửa nhà trọ</h3>
              <p className="text-xs text-gray-500">{property.titleVi || property.name}</p>
            </div>
          </div>
          <button onClick={onCancel} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

          {Object.keys(fieldErrors).length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-sm font-semibold text-amber-800">Vui lòng điền đầy đủ các trường bắt buộc</span>
              </div>
              <ul className="ml-7 text-xs text-amber-700 space-y-0.5 list-disc">
                {Object.entries(fieldErrors).map(([key, msg]) => (
                  <li key={key}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Images */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-3">Ảnh ({existingImages.length + newFiles.length})</p>
            {existingImages.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-4">
                {existingImages.map((img) => (
                  <div key={img.id} className="relative group">
                    <img src={img.url} alt={img.name} className="w-24 h-24 object-cover rounded-lg border border-gray-200" onError={(e) => { e.target.style.display = 'none'; }} />
                    <button onClick={() => onRemoveExistingImage(img.id)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600">✕</button>
                  </div>
                ))}
              </div>
            )}
            {removedImageIds.length > 0 && <p className="text-xs text-red-500 mb-3">{removedImageIds.length} ảnh sẽ bị xoá</p>}
            {newFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {newFiles.map((file, index) => (
                  <div key={index} className="relative group">
                    <img src={URL.createObjectURL(file)} alt={file.name} className="w-24 h-24 object-cover rounded-lg border-2 border-primary-300" />
                    <button onClick={() => onRemoveNewFile(index)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600">✕</button>
                  </div>
                ))}
              </div>
            )}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple onChange={onFileChange} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" />
              <p className="mt-1 text-xs text-gray-500">Thêm ảnh/video mới (jpg, png, webp, mp4, webm)</p>
            </div>
          </div>

          <hr className="border-gray-200" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tiêu đề (VI) *</label>
              <input type="text" name="titleVi" value={formData.titleVi} onChange={onInputChange} className={inputClass('titleVi')} required />
              {errorText('titleVi')}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tiêu đề (EN)</label>
              <input type="text" name="titleEn" value={formData.titleEn} onChange={onInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mô tả (VI) *</label>
              <textarea name="descriptionVi" value={formData.descriptionVi} onChange={onInputChange} rows="3" className={inputClass('descriptionVi')} required />
              {errorText('descriptionVi')}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mô tả (EN)</label>
              <textarea name="descriptionEn" value={formData.descriptionEn} onChange={onInputChange} rows="3" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Địa chỉ hiển thị (VI) *</label>
              <input type="text" name="displayAddressVi" value={formData.displayAddressVi} onChange={onInputChange} className={inputClass('addressVi')} required />
              {errorText('addressVi')}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Địa chỉ hiển thị (EN)</label>
              <input type="text" name="displayAddressEn" value={formData.displayAddressEn} onChange={onInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Địa chỉ bổ sung 1 (VI)</label>
              <input type="text" name="additionalAddress1Vi" value={formData.additionalAddress1Vi} onChange={onInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Địa chỉ bổ sung 1 (EN)</label>
              <input type="text" name="additionalAddress1En" value={formData.additionalAddress1En} onChange={onInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Địa chỉ bổ sung 2 (VI)</label>
              <input type="text" name="additionalAddress2Vi" value={formData.additionalAddress2Vi} onChange={onInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Địa chỉ bổ sung 2 (EN)</label>
              <input type="text" name="additionalAddress2En" value={formData.additionalAddress2En} onChange={onInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <input type="hidden" name="addressVi" value={formData.displayAddressVi} />
          <input type="hidden" name="addressEn" value={formData.displayAddressEn} />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Thành phố (VI)</label>
              <input type="text" name="cityVi" value={formData.cityVi} onChange={onInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Thành phố (EN)</label>
              <input type="text" name="cityEn" value={formData.cityEn} onChange={onInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quận (VI) *</label>
              <select name="districtVi" value={formData.districtVi} onChange={onInputChange} className={inputClass('districtVi')} required>
                <option value="">Chọn Quận</option>
                {Object.entries(VIETNAMESE_DISTRICT_TO_KEY).map(([vietnameseName, key]) => (
                  <option key={key} value={key}>{vietnameseName}</option>
                ))}
              </select>
              {errorText('districtVi')}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quận (EN)</label>
              <input type="text" name="districtEn" value={formData.districtEn} onChange={onInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Giá (VND) *</label>
              <input type="number" name="price" value={formData.price} onChange={onInputChange} className={inputClass('price')} placeholder="vd. 5000000" required />
              {errorText('price')}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Loại phòng *</label>
              <select name="propertyType" value={formData.propertyType} onChange={onInputChange} className={inputClass('propertyType')} required>
                <option value="">Chọn loại</option>
                {PROPERTY_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.value}</option>
                ))}
              </select>
              {errorText('propertyType')}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Điện thoại *</label>
              <input type="tel" name="phone" value={formData.phone} onChange={onInputChange} className={inputClass('phone')} required />
              {errorText('phone')}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input type="email" name="email" value={formData.email} onChange={onInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <input type="checkbox" name="available" checked={formData.available} onChange={onInputChange} className="w-4 h-4 text-primary-600 rounded" />
              <span className="text-sm font-medium text-gray-700">Còn trống</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" name="shortTermLease" checked={formData.shortTermLease} onChange={onInputChange} className="w-4 h-4 text-primary-600 rounded" />
              <span className="text-sm font-medium text-gray-700">{t('admin.shortTermLease')}</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Tiện ích</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {AMENITIES_LIST.map((amenity) => {
                const isChecked = formData.amenities.includes(amenity.key);
                return (
                  <label key={amenity.key} className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition-colors ${isChecked ? 'bg-primary-50 border-primary-300' : 'hover:bg-gray-50'}`}>
                    <input type="checkbox" checked={isChecked} onChange={() => onAmenityToggle(amenity.key)} className="w-4 h-4 text-primary-600 rounded" />
                    <span className="text-sm">{amenity.icon} {getAmenityLabel(t, amenity.key)}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Địa điểm lân cận (VI)</label>
              <input type="text" name="nearbyVi" value={formData.nearbyVi} onChange={onInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="vd. Chợ Bến Thành, Phố đi bộ Nguyễn Huệ" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Địa điểm lân cận (EN)</label>
              <input type="text" name="nearbyEn" value={formData.nearbyEn} onChange={onInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="vd. Ben Thanh Market" />
            </div>
          </div>

          {/* Secret Info - view & edit button */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Thông tin bí mật (secret.txt)
              </div>
            </label>
            <button
              type="button"
              onClick={onEditSecret}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Xem / Chỉnh sửa thông tin bí mật
            </button>
            <p className="mt-1 text-xs text-gray-500">Nhấn để xem hoặc chỉnh sửa nội dung secret.txt. Thay đổi sẽ lưu riêng biệt, không ảnh hưởng đến property.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
          <button onClick={onCancel} disabled={saving} className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">Huỷ</button>
          <button onClick={onSave} disabled={saving} className="px-6 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2">
            {saving ? (
              <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Đang lưu...</>
            ) : 'Lưu thay đổi'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyManagerPage;